import React from 'react';
import { useTranslation } from 'react-i18next';
import errorLogger from '../services/errorLogger';

/**
 * ErrorBoundary component para capturar errores de React
 * Muestra una UI amigable y loguea errores con contexto
 */
class ErrorBoundaryClass extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Actualizar state para mostrar UI de error
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Guardar información del error
        this.setState({
            error,
            errorInfo,
        });

        // Loguear el error con contexto
        errorLogger.logError(error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Mostrar UI de error amigable
            return <ErrorFallback
                onReload={this.handleReload}
                onGoHome={this.handleGoHome}
            />;
        }

        return this.props.children;
    }
}

/**
 * Componente funcional para mostrar el mensaje de error
 */
function ErrorFallback({ onReload, onGoHome }) {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="mb-4">
                    <svg
                        className="w-16 h-16 text-red-500 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">
                    {t('errorBoundary.title') || 'Oops! Algo salió mal'}
                </h1>
                <p className="text-gray-600 mb-6">
                    {t('errorBoundary.message') ||
                        'Hemos encontrado un error inesperado. Por favor, intenta recargar la página o volver al inicio.'}
                </p>
                <div className="space-y-3">
                    <button
                        onClick={onReload}
                        className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                        {t('errorBoundary.reload') || 'Recargar página'}
                    </button>
                    <button
                        onClick={onGoHome}
                        className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                        {t('errorBoundary.goHome') || 'Volver al inicio'}
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-6">
                    {t('errorBoundary.help') ||
                        'Si el problema persiste, por favor contacta con el soporte.'}
                </p>
            </div>
        </div>
    );
}

// Componente wrapper para usar hooks dentro del ErrorBoundary
function ErrorBoundary({ children }) {
    return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>;
}

export default ErrorBoundary;

